import { createStore, action, thunk, computed } from 'easy-peasy';
import fetch from 'unfetch';
import { csvParse } from 'd3-dsv';
import { group } from 'd3-array';
import { parse } from 'date-fns';

import Worker from 'worker-loader!./worker.js';
import config from '~/config/article';

const parseCsvRow = (row) => {
  row.lat = +row.lat;
  row.lon = +row.lon;
  row.confirmed = +row.confirmed;
  row.recovered = +row.recovered;
  row.deaths = +row.deaths;
  row.updated = +row.updated;
  row.levels = row.levels ? row.levels.split(',').map((level) => +level) : [];
  row.population = +row.population;

  return row;
};

const parseCsvData = (csv) => {
  return csvParse(csv, parseCsvRow).sort((a, b) => b.confirmed - a.confirmed);
};

const loadCurrentData = async () => {
  const response = await fetch(
    config.sources.current + ('?t=' + Math.floor(Date.now() / 300000).toString(36))
  );
  const csv = await response.text();
  const data = parseCsvData(csv);
  return data;
};

const loadTotalData = async (v = 1) => {
  const sourceKey = `totals_v${v}`;

  const response = await fetch(
    config.sources[sourceKey] + ('?t=' + Math.floor(Date.now() / 300000).toString(36))
  );
  const csv = await response.text();
  const data = csvParse(csv, (row) => ({
    date: parse(row.date, 'yyyyMMdd', new Date()).getTime(),
    confirmed: +row.confirmed,
    recovered: +row.recovered,
    deaths: +row.deaths,
    label: row.label,
  }));

  return data;
};

const loadHeatmapData = async () => {
  const response = await fetch(
    config.sources.heatmap + ('?t=' + Math.floor(Date.now() / 300000).toString(36))
  );
  const csv = await response.text();
  const data = csvParse(csv, (row) => {
    return {
      ...row,
      population: +row.population,
      cases: +row.cases,
      infections: +row.infections,
      cases_active: +row.cases - +row.prev,
    };
  });
  return data;
};

function getIsTeaser() {
  if (typeof window === 'undefined') {
    return false;
  }
  return !!document.getElementById('teaser-widget') || process.env.IS_TEASER;
}

const DataStore = createStore({
  data: {
    current: [],
    currentLoading: computed((state) => state.current.length <= 0),
    history: null,
    historyMap: computed((state) => {
      if (!state.history) {
        return null;
      }

      return group(state.history, (d) => d.date);
    }),
    heatmapData: [],
    kreiseInfectionsMap: computed((state) => {
      const items = state.heatmapData.map((d) => [d.ags, d]);
      return new Map(items);
    }),
    historyLoadStarted: false,
    historyLoading: computed((state) => !state.history),

    totalData: null,
    totalDataV2: null,
    totalsLoading: computed((state) => !state.totalData || !state.totalDataV2),

    loadDataStart: action((state) => {
      state.current = [];
    }),

    loadCurrentDataSuccess: action((state, payload) => {
      state.current = payload;
    }),

    loadHistoryDataStart: action((state) => {
      state.historyLoadStarted = true;
    }),

    loadHistoryDataSuccess: action((state, payload) => {
      state.history = payload;
    }),

    loadHeatmapDataSuccess: action((state, payload) => {
      state.heatmapData = payload;
    }),

    loadTotalDataSuccess: action((state, payload) => {
      state.totalData = payload.v1;
      state.totalDataV2 = payload.v2;
    }),

    loadData: thunk(async (actions, payload, helpers) => {
      if (helpers.getState().current.length) {
        return false;
      }

      actions.loadDataStart();
      const currentData = await loadCurrentData();
      actions.loadCurrentDataSuccess(currentData);
      const totalDataV1 = await loadTotalData(1);
      const totalDataV2 = await loadTotalData(2);
      actions.loadTotalDataSuccess({ v1: totalDataV1, v2: totalDataV2 });
      const heatmapData = await loadHeatmapData();
      actions.loadHeatmapDataSuccess(heatmapData);
    }),
    loadHistoryData: thunk(async (actions, _, helpers) => {
      if (helpers.getState().historyLoadStarted) {
        return false;
      }

      actions.loadHistoryDataStart();

      const response = await fetch(
        config.sources.history + ('?t=' + Math.floor(Date.now() / 300000).toString(36))
      );
      const data = await response.text();

      const worker = new Worker();

      worker.postMessage({ rawText: data });

      worker.addEventListener('message', ({ data: { historyData } }) => {
        actions.loadHistoryDataSuccess(historyData);
        worker.terminate();
      });
    }),
  },
  app: {
    isTrendView: false,
    isTeaser: getIsTeaser(),
    setTrendView: action((state, payload) => {
      state.isTrendView = payload;
    }),
    toggleTrendView: action((state) => {
      state.isTrendView = !state.isTrendView;
    }),
  },
});

export default DataStore;
